import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { Permission } from '@/types';
import { hasUserPermission, canAccessProjects, canActAsProvider } from '@/lib/permissions';
import type { SidebarLink } from '@/layout/Sidebar';
import { Role } from '@/types';

// Lazy load pages for performance
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const VerifyEmail = lazy(() => import('@/pages/VerifyEmail'));
const ProviderRegister = lazy(() => import('@/pages/ProviderRegister'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const UnauthorizedAccount = lazy(() => import('@/pages/UnauthorizedAccount'));
const AuthCallback = lazy(() => import('@/pages/AuthCallback'));

// Pages unifiées
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Projects = lazy(() => import('@/pages/Projects'));
const ProjectDetail = lazy(() => import('@/pages/ProjectDetail'));
const EditorialCalendar = lazy(() => import('@/pages/EditorialCalendarAdmin'));

// Pages admin uniquement
const ClientsAdmin = lazy(() => import('@/pages/ClientsAdmin'));
const UsersAdmin = lazy(() => import('@/pages/UsersAdmin'));
const InvoicesAdmin = lazy(() => import('@/pages/InvoicesAdmin'));
const AuditLogsAdmin = lazy(() => import('@/pages/AuditLogsAdmin'));
const MarginsAdmin = lazy(() => import('@/pages/MarginsAdmin'));
const Tache = lazy(() => import('@/pages/Tache'));

// Pages prestataire/provider
const InvoicesPrestataire = lazy(() => import('@/pages/InvoicesPrestataire'));
const InfosPrestataire = lazy(() => import('@/pages/InfosPrestataire'));

// Pages client
const ClientInfo = lazy(() => import('@/pages/ClientInfo'));

// Public routes configuration
export const publicRoutes: RouteObject[] = [
  { path: '/register', element: <Register /> },
  { path: '/verify-email', element: <VerifyEmail /> },
  { path: '/register-provider', element: <ProviderRegister /> },
  { path: '/login', element: <Login /> },
  { path: '/reset-password', element: <ResetPassword /> },
  { path: '/unauthorized', element: <UnauthorizedAccount /> },
  { path: '/auth/callback', element: <AuthCallback /> },
  { path: '*', element: <Navigate to="/login" replace /> },
];

/**
 * Génère les liens de la sidebar selon les permissions de l'utilisateur
 * Plus besoin de vérifier le rôle, on vérifie les permissions
 */
export const getSidebarLinks = (role: Role, permissions: Permission[] | undefined): SidebarLink[] => {
  const links: SidebarLink[] = [];

  // Dashboard - toujours visible
  links.push({ to: '/', label: 'Dashboard', icon: <span aria-hidden="true">{'🏠︎'}</span> });

  // Projets - visible pour tous ceux qui ont accès à des projets
  if (canAccessProjects(role, permissions)) {
    links.push({ to: '/projects', label: 'Projets', icon: <span aria-hidden="true">{'📁︎'}</span> });
  }

  // Tâches - accessible à tous ceux qui ont accès aux projets
  if (canAccessProjects(role, permissions)) {
    links.push({ to: '/tache', label: 'Tâches', icon: <span aria-hidden="true">{'✅'}</span> });
  }

  // Calendrier éditorial - visible si accès aux projets
  if (canAccessProjects(role, permissions)) {
    links.push({ to: '/editorial-calendar', label: 'Calendrier Éditorial', icon: <span aria-hidden="true">{'🚀'}</span> });
  }

  // Entreprises - admin uniquement
  if (hasUserPermission(role, permissions, Permission.MANAGE_CLIENTS)) {
    links.push({ to: '/clients', label: 'Entreprises', icon: <span aria-hidden="true">{'🏢'}</span> });
  }

  // Utilisateurs - admin uniquement
  if (hasUserPermission(role, permissions, Permission.MANAGE_USERS)) {
    links.push({ to: '/users', label: 'Utilisateurs', icon: <span aria-hidden="true">{'😊'}</span> });
  }

  // Journaux d'audit - admin uniquement
  if (hasUserPermission(role, permissions, Permission.MANAGE_USERS)) {
    links.push({ to: '/audit-logs', label: 'Journaux d\'Audit', icon: <span aria-hidden="true">{'🕵️‍♂️'}</span> });
  }

  // Factures admin - validation des factures
  if (hasUserPermission(role, permissions, Permission.VALIDATE_INVOICES)) {
    links.push({ to: '/invoices', label: 'Factures', icon: <span aria-hidden="true">{'💰'}</span> });
  }

  if (hasUserPermission(role, permissions, Permission.VIEW_MARGINS_OVERVIEW)) {
    links.push({ to: '/marges', label: 'Marges', icon: <span aria-hidden="true">{'📈'}</span> });
  }

  // Mes factures - prestataires ou chefs avec ACT_AS_PROVIDER
  if (canActAsProvider(role, permissions)) {
    // Ne pas afficher si on a déjà VALIDATE_INVOICES (admin voit /invoices)
    if (!hasUserPermission(role, permissions, Permission.VALIDATE_INVOICES)) {
      links.push({ to: '/my-invoices', label: 'Mes Factures', icon: <span aria-hidden="true">{'💰'}</span> });
    }
  }

  // Mes infos - prestataires ou chefs avec ACT_AS_PROVIDER
  if (canActAsProvider(role, permissions)) {
    links.push({ to: '/provider-info', label: 'Mes Infos', icon: <span aria-hidden="true">{'😊'}</span> });
  }

  // Mes infos - clients uniquement
  if (role === Role.CLIENT) {
    links.push({ to: '/client-info', label: 'Mes Infos', icon: <span aria-hidden="true">{'😊'}</span> });
  }

  return links;
};

/**
 * Génère les routes selon les permissions de l'utilisateur
 * Architecture RBAC : les routes sont basées sur les permissions, pas les rôles
 */
export const getRoleBasedRoutes = (role: Role, permissions: Permission[] | undefined): RouteObject[] => {
  const routes: RouteObject[] = [];

  // Dashboard unifié - toujours accessible
  routes.push({ path: '/', element: <Dashboard /> });

  // Projets - accessibles selon les permissions
  if (canAccessProjects(role, permissions)) {
    routes.push(
      { path: '/projects', element: <Projects /> },
      { path: '/projects/:id', element: <ProjectDetail /> }
    );
  }

  // Calendrier éditorial
  if (canAccessProjects(role, permissions)) {
    routes.push({ path: '/editorial-calendar', element: <EditorialCalendar /> });
  }

  // Routes admin uniquement
  if (hasUserPermission(role, permissions, Permission.MANAGE_CLIENTS)) {
    routes.push({ path: '/clients', element: <ClientsAdmin /> });
  }

  if (hasUserPermission(role, permissions, Permission.MANAGE_USERS)) {
    routes.push({ path: '/users', element: <UsersAdmin /> });
  }

  if (hasUserPermission(role, permissions, Permission.MANAGE_USERS)) {
    routes.push({ path: '/audit-logs', element: <AuditLogsAdmin /> });
  }

  if (hasUserPermission(role, permissions, Permission.VALIDATE_INVOICES)) {
    routes.push({ path: '/invoices', element: <InvoicesAdmin /> });
  }

  if (hasUserPermission(role, permissions, Permission.VIEW_MARGINS_OVERVIEW)) {
    routes.push({ path: '/marges', element: <MarginsAdmin /> });
  }

  // Tâches - accessible à tous ceux qui ont accès aux projets
  if (canAccessProjects(role, permissions)) {
    routes.push({ path: '/tache', element: <Tache /> });
  }

  // Routes prestataire/provider
  if (canActAsProvider(role, permissions)) {
    routes.push(
      { path: '/my-invoices', element: <InvoicesPrestataire /> },
      { path: '/provider-info', element: <InfosPrestataire /> }
    );
  }

  // Routes client uniquement
  if (role === Role.CLIENT) {
    routes.push({ path: '/client-info', element: <ClientInfo /> });
  }

  // Fallback - rediriger vers le dashboard
  routes.push({ path: '*', element: <Navigate to="/" replace /> });

  return routes;
};

/**
 * Routes servies à un visiteur arrivé par lien public.
 *
 * Ce sont exactement les routes du rôle client, moins « Mes Infos » : cette
 * page décrit le compte utilisateur, qui n'existe pas dans ce contexte.
 * Le routeur tourne avec un basename `/p/<slug>-<token>`, donc les chemins
 * restent ceux de l'application (`/`, `/projects/:id`, ...).
 */
export const getPublicLinkRoutes = (): RouteObject[] =>
  getRoleBasedRoutes(Role.CLIENT, []).filter((route) => route.path !== '/client-info');

/** Menu latéral d'une visite par lien public : celui du client, sans « Mes Infos » */
export const getPublicLinkSidebarLinks = (): SidebarLink[] =>
  getSidebarLinks(Role.CLIENT, []).filter((link) => link.to !== '/client-info');
